import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, Avatar, IconButton, Menu, MenuItem, Divider, Snackbar, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  AdminPanelSettings as AdminIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { validateSession, logout, api } from '../../utils/api';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [accessDeniedOpen, setAccessDeniedOpen] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('idToken');
      if (!token) {
        navigate('/login?redirect=/admin');
        return;
      }

      const { valid, user: userData } = await validateSession();
      if (!valid) {
        navigate('/login?redirect=/admin');
        return;
      }

      // Session is valid. Fetch the user's role from the backend.
      // Role lives in Postgres users.role (not in the JWT), so we hit /users/profile.
      try {
        const response = await api.get('/users/profile');
        if (!response.ok) {
          // 401/403 → session truly invalid. 5xx → treat as auth failure to avoid leaking admin UI on backend hiccup.
          navigate('/login?redirect=/admin');
          return;
        }
        const data = await response.json();
        const role = data?.profile?.role;

        if (role !== 'admin' && role !== 'super_admin') {
          // Authenticated but not an admin. Show snackbar, then redirect.
          setAccessDeniedOpen(true);
          setTimeout(() => navigate('/admin'), 1500);
          return;
        }

        // Admin or super_admin. Merge the role into user state and render.
        setUser({ ...userData, ...data.profile });
        setChecking(false);
      } catch (err) {
        console.error('Error checking admin role:', err);
        navigate('/login?redirect=/admin');
      }
    };

    checkAuth();
  }, [navigate]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
  };

  const handleBackToApp = () => {
    window.location.href = '/admin';
  };

  if (checking) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Admin Header */}
      <Box
        sx={{
          bgcolor: '#1a1a2e',
          color: 'white',
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        {/* Left side - Logo and title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AdminIcon sx={{ fontSize: 32, color: '#2D6E6A' }} />
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2 }}>
              Percoro Admin
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              System Management Console
            </Typography>
          </Box>
        </Box>

        {/* Right side - User menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={handleBackToApp}
            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
            title="Back to App"
          >
            <HomeIcon />
          </IconButton>
          
          <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
            <Avatar
              sx={{ width: 36, height: 36, bgcolor: '#2D6E6A' }}
            >
              {user?.name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'A'}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {user?.name || user?.email || 'Admin'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleBackToApp}>
              <HomeIcon fontSize="small" sx={{ mr: 1 }} />
              Back to App
            </MenuItem>
            <MenuItem onClick={() => navigate('/admin/profile')}>
              <PersonIcon fontSize="small" sx={{ mr: 1 }} />
              My Profile
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Main Content */}
      <Box>{children}</Box>

      {/* Access denied snackbar */}
      <Snackbar
        open={accessDeniedOpen}
        autoHideDuration={4000}
        onClose={() => setAccessDeniedOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
          Admin access required
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminLayout;
